import React from "react";
import cloneDeep from "lodash/cloneDeep";
import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Link from "@cloudscape-design/components/link";
import Box from "@cloudscape-design/components/box";
import Grid from "@cloudscape-design/components/grid";
import Badge from "@cloudscape-design/components/badge";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Alert from "@cloudscape-design/components/alert";
import Spinner from "@cloudscape-design/components/spinner";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import Modal from "@cloudscape-design/components/modal";
/* Recoil */
import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import {  providerStore } from "../stores/provider";
import { getEmptyProviderEditValues, providerEditModalStore } from "../stores/providerEditModal";
import { providerAddModalStore } from "../stores/providerAddModal";
import { userStore } from "../stores/access";
import { parseStoredProviderCodeArray, parseStoredProviderCountries } from "./providerValidation";
/* ------- */

class Providers extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			selectedItems: [],
			depositRestriction: {
				visible: false,
				loading: false,
				providersLoading: false,
				saving: false,
				providerOptions: [],
				providerSearchText: "",
				selectedProviders: [],
				depositFrom: "0",
				periodDays: "",
				error: "",
			},
		};
		this.providerSearchTimer = null;
		this.providerSearchRequestId = 0;
	}

	componentDidMount() {
		this.props.Interface.loadProvider();
	}

	componentWillUnmount() {
		clearTimeout(this.providerSearchTimer);
		this.providerSearchRequestId += 1;
	}

	openProviderEdit = (item) => {
		const values = getEmptyProviderEditValues();
		const storedErrors = {};
		if (item) {
			const restrictedCountries = parseStoredProviderCountries(item.restrictedCountries);
			const currency = parseStoredProviderCodeArray(item.currency);
			if (restrictedCountries.error) storedErrors.restrictedCountries = { raw: item.restrictedCountries, text: restrictedCountries.error };
			if (currency.error) storedErrors.currency = { raw: item.currency, text: currency.error };
			const booleanOption = (value) => {
				const booleanValue = value === 1 ? true : value === 0 ? false : value;
				return { value: booleanValue, label: booleanValue === true ? "True" : booleanValue === false ? "False" : String(value) };
			};
			Object.assign(values, {
				name: item.name,
				path: item.path,
				order: String(item.order),
				listed: booleanOption(item.listed),
				active: booleanOption(item.active),
				integrationType: item.integrationType,
				restrictedCountries: restrictedCountries.error ? null : restrictedCountries.value,
				currency: currency.error ? null : currency.value,
				fee: String(item.fee),
			});
		}
		this.props.setProviderEditModal({
			...this.props.providerEditModal,
			show: true,
			loaded: true,
			mode: item ? "single" : "bulk",
			selectedIds: item ? [item.id] : this.state.selectedItems.map((provider) => provider.id),
			values,
			initialValues: item ? cloneDeep(values) : null,
			storedErrors,
			error: { text: "", code: 0, timestamp: 0 },
		});
	};

	handleKeyPress = (event) => {
		if (event.detail.key === "Enter") {
			this.props.setProvider({ ...this.props.provider, pagination: { ...this.props.provider.pagination, current: 1 } });
			setTimeout(() => {
				this.props.Interface.loadProvider();
			}, 0);
		}
	};

	canManageDepositRestriction = () => {
		const { user } = this.props;
		const showColumn = Array.isArray(user.showColumn) ? user.showColumn : [];

		return user.mode === "brand" && user.brand && (Number(user.level) >= 6 || showColumn.includes("providers_restriction"));
	};

	getProviderOptions = (providers) => {
		return providers.map((provider) => ({
			label: provider.name,
			value: String(provider.id),
			labelTag: `#${provider.id}`,
		}));
	};

	mergeProviderOptions = (...optionLists) => {
		const options = new Map();

		optionLists.flat().forEach((option) => {
			options.set(option.value, option);
		});

		return [...options.values()];
	};

	openDepositRestriction = async () => {
		clearTimeout(this.providerSearchTimer);
		const requestId = ++this.providerSearchRequestId;
		this.setState((state) => ({
			depositRestriction: {
				...state.depositRestriction,
				visible: true,
				loading: true,
				providersLoading: false,
				providerSearchText: "",
				error: "",
			},
		}));

		try {
			const [config, providers] = await Promise.all([
				this.props.Interface.getProviderDepositRestriction(),
				this.props.Interface.loadProviderOptions(),
			]);
			const selectedIds = new Set((config.providerIds || []).map(String));
			const loadedIds = new Set(providers.map((provider) => String(provider.id)));
			const missingSelectedIds = [...selectedIds].filter((providerId) => !loadedIds.has(providerId));
			const selectedProviders = missingSelectedIds.length
				? await this.props.Interface.loadProviderOptions("", missingSelectedIds)
				: [];
			if (requestId !== this.providerSearchRequestId) return;

			const providerOptions = this.getProviderOptions([...providers, ...selectedProviders]);

			this.setState((state) => ({
				depositRestriction: {
					...state.depositRestriction,
					loading: false,
					providerOptions,
					selectedProviders: providerOptions.filter((provider) => selectedIds.has(provider.value)),
					depositFrom: String(config.depositFrom || 0),
					periodDays: config.periodDays ? String(config.periodDays) : "",
				},
			}));
		} catch (error) {
			if (requestId !== this.providerSearchRequestId) return;

			this.setState((state) => ({
				depositRestriction: {
					...state.depositRestriction,
					loading: false,
					error: error.text || error.message || String(error),
				},
			}));
		}
	};

	searchProviders = (filteringText) => {
		const searchText = String(filteringText || "");
		const { depositRestriction } = this.state;

		if (
			searchText === depositRestriction.providerSearchText &&
			(depositRestriction.providersLoading || depositRestriction.providerOptions.length) &&
			!depositRestriction.error
		) {
			return;
		}

		clearTimeout(this.providerSearchTimer);
		const requestId = ++this.providerSearchRequestId;
		this.setState((state) => ({
			depositRestriction: {
				...state.depositRestriction,
				providersLoading: true,
				providerSearchText: searchText,
				error: "",
			},
		}));

		this.providerSearchTimer = setTimeout(async () => {
			try {
				const providers = await this.props.Interface.loadProviderOptions(searchText);
				if (requestId !== this.providerSearchRequestId) return;

				const providerOptions = this.getProviderOptions(providers);
				this.setState((state) => ({
					depositRestriction: {
						...state.depositRestriction,
						providersLoading: false,
						providerOptions: this.mergeProviderOptions(
							state.depositRestriction.selectedProviders,
							providerOptions,
						),
					},
				}));
			} catch (error) {
				if (requestId !== this.providerSearchRequestId) return;

				this.setState((state) => ({
					depositRestriction: {
						...state.depositRestriction,
						providersLoading: false,
						error: error.text || error.message || String(error),
					},
				}));
			}
		}, 400);
	};

	closeDepositRestriction = () => {
		if (this.state.depositRestriction.saving) return;

		clearTimeout(this.providerSearchTimer);
		this.providerSearchRequestId += 1;
		this.setState((state) => ({
			depositRestriction: {
				...state.depositRestriction,
				visible: false,
				providersLoading: false,
				error: "",
			},
		}));
	};

	saveDepositRestriction = async () => {
		const { depositRestriction } = this.state;
		const depositFrom = Number(depositRestriction.depositFrom);
		const periodDays = depositRestriction.periodDays === "" ? null : Number(depositRestriction.periodDays);

		if (!Number.isFinite(depositFrom) || depositFrom < 0) {
			this.setState((state) => ({
				depositRestriction: {
					...state.depositRestriction,
					error: "Deposit from must be zero or greater.",
				},
			}));
			return;
		}

		if (periodDays !== null && (!Number.isSafeInteger(periodDays) || periodDays <= 0)) {
			this.setState((state) => ({
				depositRestriction: {
					...state.depositRestriction,
					error: "Period must be a positive integer or empty.",
				},
			}));
			return;
		}

		if (depositFrom > 0 && depositRestriction.selectedProviders.length === 0) {
			this.setState((state) => ({
				depositRestriction: {
					...state.depositRestriction,
					error: "Select at least one provider.",
				},
			}));
			return;
		}

		this.setState((state) => ({
			depositRestriction: {
				...state.depositRestriction,
				saving: true,
				error: "",
			},
		}));

		try {
			await this.props.Interface.saveProviderDepositRestriction({
				providerIds: depositRestriction.selectedProviders.map((provider) => Number(provider.value)),
				depositFrom,
				periodDays,
			});
			clearTimeout(this.providerSearchTimer);
			this.providerSearchRequestId += 1;
			this.props.Interface.addNoty("Success", "Provider deposit restriction was successfully saved");
			this.setState((state) => ({
				depositRestriction: {
					...state.depositRestriction,
					visible: false,
					saving: false,
				},
			}));
		} catch (error) {
			this.setState((state) => ({
				depositRestriction: {
					...state.depositRestriction,
					saving: false,
					error: error.text || error.message || String(error),
				},
			}));
		}
	};



	render() {
		const { depositRestriction, selectedItems } = this.state;

		return (
			<>
				<Modal
					visible={depositRestriction.visible}
					header="Provider deposit restriction"
					onDismiss={this.closeDepositRestriction}
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button disabled={depositRestriction.saving} onClick={this.closeDepositRestriction}>
									Cancel
								</Button>
								<Button variant="primary" loading={depositRestriction.saving} disabled={depositRestriction.loading} onClick={this.saveDepositRestriction}>
									Save
								</Button>
							</SpaceBetween>
						</Box>
					}>
					{depositRestriction.error ? (
						<Box margin={{ bottom: "m" }}>
							<Alert type="error">{depositRestriction.error}</Alert>
						</Box>
					) : null}
					{depositRestriction.loading ? (
						<Box textAlign="center">
							<Spinner size="large" />
						</Box>
					) : (
						<SpaceBetween size="m">
							<FormField label="Providers" description="Select one or more providers whose slots require deposits.">
								<Multiselect
									filteringType="manual"
									placeholder="Choose providers"
									statusType={depositRestriction.providersLoading ? "loading" : "finished"}
									selectedOptions={depositRestriction.selectedProviders}
									options={depositRestriction.providerOptions}
									onLoadItems={({ detail }) => this.searchProviders(detail.filteringText)}
									onChange={({ detail }) => {
										this.setState((state) => ({
											depositRestriction: {
												...state.depositRestriction,
												selectedProviders: detail.selectedOptions,
											},
										}));
									}}
								/>
							</FormField>
							<FormField label="Deposit from, RUB" description="Set to 0 to disable the restriction.">
								<Input
									type="number"
									value={depositRestriction.depositFrom}
									onChange={({ detail }) => {
										this.setState((state) => ({
											depositRestriction: {
												...state.depositRestriction,
												depositFrom: detail.value,
											},
										}));
									}}
								/>
							</FormField>
							<FormField label="Period, days" description="Leave empty to check deposits for all time.">
								<Input
									type="number"
									value={depositRestriction.periodDays}
									onChange={({ detail }) => {
										this.setState((state) => ({
											depositRestriction: {
												...state.depositRestriction,
												periodDays: detail.value,
											},
										}));
									}}
								/>
							</FormField>
						</SpaceBetween>
					)}
				</Modal>
				{this.props.provider.loaded && this.props.provider.error.text.length ? (
					<Box
						margin={{ bottom: "m" }}
						children={
							<Alert
								type="error"
								header="Error loading data:"
								children={
									<>
										<Box variant="p" children={this.props.provider.error.text} />
										<hr />
										<Box
											variant="p"
											children={
												<>
													request error code: {this.props.provider.error.code} | request timestamp: {this.props.provider.error.timestamp}
												</>
											}
										/>
									</>
								}
							/>
						}
					/>
				) : (
					<></>
				)}


				<Table
					stickyHeader="true"
					trackBy="id"
					loading={!this.props.provider.loaded}
					sortingColumn={{ sortingField: this.props.provider.sorting.sortingColumn.column }}
					sortingDescending={this.props.provider.sorting.isDescending}
					onSortingChange={(sorting) => {
						this.props.setProvider({
							...this.props.provider,
							sorting: {
								...this.props.provider.sorting,
								sortingColumn: {
									order: !sorting.detail.isDescending ? "ASC" : "DESC",
									column: sorting.detail.sortingColumn.sortingField,
								},
								isDescending: sorting.detail.isDescending,
							},
						});
						setTimeout(() => {
							this.props.Interface.loadProvider();
						}, 250);
					}}
					columnDefinitions={[
						{
							id: "id",
							header: "ID",
							sortingField: "id",
							cell: (item) => item.id,
						},
						{ id: "name", header: "Name", sortingField: "name", cell: (item) => <Link onFollow={() => this.openProviderEdit(item)}>{item.name}</Link> },
						{ id: "path", header: "Path", sortingField: "path", cell: (item) => item.path },
						{
							id: "order",
							header: "Order",
							sortingField: "order",
							cell: (item) => item.order,
						},
						{
							id: "listed",
							header: "Listed",
							sortingField: "listed",
							cell: (item) => item.listed,
						},
						{
							id: "active",
							header: "Active",
							sortingField: "active",
							cell: (item) => item.active,
						},
						{
							id: "integrationType",
							header: "Integration type",
							sortingField: "integrationType",
							cell: (item) => item.integrationType,
						},
						{
							id: "restrictedCountries",
							header: "Restricted Countries",
							sortingField: "restrictedCountries",
							cell: (item) => item.restrictedCountries,
						},
						{
							id: "currency",
							header: "Currency",
							sortingField: "currency",
							cell: (item) => item.currency,
						},
						{
							id: "fee",
							header: "Fee",
							sortingField: "fee",
							cell: (item) => item.fee.toFixed(2),
						},
					]}
					items={this.props.provider.data}
					selectionType="multi"
					selectedItems={selectedItems}
					onSelectionChange={({ detail }) =>
						this.setState({ selectedItems: detail.selectedItems })
					}
					preferences={
						<Preferences
							cancelLabel="Cancel"
							confirmLabel="Confirm"
							pageSizePreference={{ title: "Providers per page", options: this.props.config.provider.preferences.perPage }}
							preferences={{ pageSize: this.props.config.provider.perPage }}
							title="Preferences"
							onConfirm={(preferences) => {
								localStorage.setItem("provider.perPage", preferences.detail.pageSize);
								this.props.setConfig({
									...this.props.config,
									provider: { ...this.props.config.provider, perPage: preferences.detail.pageSize },
								});
								setTimeout(() => {
									this.props.Interface.loadProvider();
								}, 250);
							}}
						/>
					}
					pagination={
						<Pagination
							disabled={!this.props.provider.loaded}
							currentPageIndex={this.props.provider.pagination.current}
							pagesCount={this.props.provider.pagination.total}
							onChange={(page) => {
								this.props.setProvider({
									...this.props.provider,
									pagination: { ...this.props.provider.pagination, current: page.detail.currentPageIndex },
								});
								setTimeout(() => {
									this.props.Interface.loadProvider();
								}, 250);
							}}
						/>
					}
					filter={
						<Box
							margin={{ bottom: "m" }}
							children={
								<Grid
									gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}
									children={
										<>
											<Select
												filteringType="auto"
												disabled={!this.props.provider.loaded}
												placeholder="Type"
												selectedOption={this.props.provider.currentSearchType}
												onChange={(option) => {
													option.detail.selectedOption.type !== this.props.provider.currentSearchType &&
													this.props.setProvider({
														...this.props.provider,
														currentSearchType: option.detail.selectedOption,
													});
												}}
												options={this.props.provider.currentOption.type}
											/>
											<Input
												disabled={!this.props.provider.loaded}
												onKeyDown={this.handleKeyPress}
												disableBrowserAutocorrect={true}
												placeholder="id, name"
												inputMode="search"
												type="search"
												value={this.props.provider.currentSearch}
												onChange={(event) => {
													this.props.setProvider({
														...this.props.provider,
														currentSearch: event.detail.value,
													});
												}}
											/>
										</>
									}
								/>
							}
						/>
					}
					header={
						<Box
							margin={{ bottom: "m" }}
							children={
								<Header
									children="Providers"
									counter={<>({this.props.provider.pagination.count})</>}
									actions={
										<SpaceBetween direction="horizontal" size="l">
											{this.canManageDepositRestriction() ? (
												<Button
													iconName="lock-private"
													disabled={!this.props.provider.loaded}
													onClick={this.openDepositRestriction}
													children="Deposit restriction"
												/>
											) : null}
											<Button
												disabled={!this.props.provider.loaded}
												className={[this.props.styles["players-search-button"], this.props.styles["btn"]].join(" ")}
												onClick={() => {
													this.props.setProviderAddModal({
														...this.props.providerAddModal,
														show: true,
													});
												}}
												variant="primary"
												children="Add"
											/>
											<Button
												iconName="settings"
												disabled={!this.props.provider.loaded}
												className={[this.props.styles["players-search-button"], this.props.styles["btn"]].join(" ")}
												onClick={() => this.openProviderEdit()}
												variant="primary"
												children="Edit"
											/>
										</SpaceBetween>
									}
								/>
							}
						/>
					}
					empty="No providers found with requested filter"
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const provider = useRecoilValue(providerStore);
		const setProvider = useSetRecoilState(providerStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const user = useRecoilValue(userStore);

		const providerEditModal = useRecoilValue(providerEditModalStore);
		const setProviderEditModal = useSetRecoilState(providerEditModalStore);

		const providerAddModal = useRecoilValue(providerAddModalStore);
		const setProviderAddModal = useSetRecoilState(providerAddModalStore);

		return (
			<Component
				{...props}
				setProviderEditModal={setProviderEditModal}
				providerEditModal={providerEditModal}
				provider={provider}
				setProvider={setProvider}
				providerAddModal={providerAddModal}
				setProviderAddModal={setProviderAddModal}
				config={config}
				setConfig={setConfig}
				user={user}
			/>
		);
	};
}

export default withRecoil(Providers);
